import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  EmployeePracticeAreaDto,
  EmployeePracticeAreaSource,
} from '../../../models/resource.models';
import { PracticeArea } from '../../../models/user.models';
import { AuthService } from '../../../services/auth.service';
import { PracticeAreaService } from '../../../services/practice-area.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { describeApiError } from '../../../utils/api-error.utils';

export interface UserPracticeAreasDialogData {
  employeeId: string;
  userName: string;
  practiceAreas: EmployeePracticeAreaDto[];
}

/** A single row in the merged list. `primary`/`secondary` are assigned (primary
 *  is locked); `available` can be added. */
type RowKind = 'primary' | 'secondary' | 'available';

interface PracticeAreaRow {
  code: string;
  name: string;
  kind: RowKind;
}

@Component({
  selector: 'app-user-practice-areas-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
  ],
  templateUrl: './user-practice-areas-dialog.component.html',
  styleUrls: ['./user-practice-areas-dialog.component.scss'],
})
export class UserPracticeAreasDialogComponent implements OnInit {
  private dialogRef =
    inject<MatDialogRef<UserPracticeAreasDialogComponent, boolean>>(
      MatDialogRef,
    );
  data = inject<UserPracticeAreasDialogData>(MAT_DIALOG_DATA);
  private practiceAreaService = inject(PracticeAreaService);
  private snackbarService = inject(SnackbarService);
  private authService = inject(AuthService);

  isLoading = false;
  /** All active practice areas in the system, used as the add-search source. */
  private allPracticeAreas: PracticeArea[] = [];
  /** The user's current assignments (kept in sync as we add/remove). */
  assigned: EmployeePracticeAreaDto[] = [];

  searchQuery = '';
  /**
   * The rendered, position-stable rows. Re-sorted (assigned-first) only on load
   * and on search change; adding/removing mutates a row's kind in place so it
   * holds its position in the list until the filter changes or the dialog reopens.
   */
  displayRows: PracticeAreaRow[] = [];
  /** Codes with an in-flight add/remove request. */
  loadingCodes = new Set<string>();
  /** Whether any change was made (so the parent can refresh on close). */
  private changed = false;

  get canManage(): boolean {
    return this.authService.canManagePracticeAreas();
  }

  /** The primary (employee-sync) practice area(s) shown in the summary band. */
  get primaryName(): string {
    const names = this.assigned
      .filter((a) => this.isLocked(a))
      .map((a) => a.practiceAreaName);
    return names.length ? names.join(', ') : 'None';
  }

  /** Count of secondary (manually added) practice areas, shown beneath the primary. */
  get additionalCount(): number {
    return this.assigned.filter((a) => !this.isLocked(a)).length;
  }

  ngOnInit(): void {
    this.assigned = [...(this.data.practiceAreas ?? [])];
    this.isLoading = true;
    this.practiceAreaService.getAllPracticeAreas().subscribe({
      next: (areas) => {
        this.allPracticeAreas = areas.filter((a) => a.isActive);
        this.rebuildRows();
        this.isLoading = false;
      },
      error: () => {
        this.allPracticeAreas = [];
        this.isLoading = false;
        this.snackbarService.showError('Failed to load practice areas');
      },
    });
  }

  /** A practice area is locked (non-removable) when it was sourced from the
   *  employee sync rather than added manually in the app. */
  private isLocked(area: EmployeePracticeAreaDto): boolean {
    return area.source === EmployeePracticeAreaSource.EmployeeSync;
  }

  isCodeLoading(code: string): boolean {
    return this.loadingCodes.has(code);
  }

  /**
   * Rebuild the merged, search-filtered list (assigned first — primary, then
   * secondary — followed by available). Called only on load and on search
   * change, so add/remove can hold a row's position between rebuilds.
   */
  private rebuildRows(): void {
    const query = this.searchQuery.trim().toLowerCase();
    const rows: PracticeAreaRow[] = this.assigned
      .filter((a) => !this.isLocked(a))
      .map((a) => ({
        code: a.practiceAreaCode,
        name: a.practiceAreaName,
        kind: 'secondary' as const,
      }));

    if (this.canManage) {
      const assignedCodes = new Set(
        this.assigned.map((a) => a.practiceAreaCode),
      );
      this.allPracticeAreas
        .filter((a) => !assignedCodes.has(a.practiceCode))
        .forEach((a) =>
          rows.push({
            code: a.practiceCode,
            name: a.description,
            kind: 'available',
          }),
        );
    }

    const weight: Record<RowKind, number> = {
      primary: 0,
      secondary: 1,
      available: 2,
    };
    this.displayRows = rows
      .filter(
        (r) =>
          !query ||
          r.name.toLowerCase().includes(query) ||
          r.code.toLowerCase().includes(query),
      )
      .sort(
        (a, b) =>
          weight[a.kind] - weight[b.kind] || a.name.localeCompare(b.name),
      );
  }

  onSearchChange(): void {
    this.rebuildRows();
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.rebuildRows();
  }

  /** Update a rendered row's kind in place, keeping its current position. */
  private setRowKind(code: string, kind: RowKind): void {
    this.displayRows = this.displayRows.map((r) =>
      r.code === code ? { ...r, kind } : r,
    );
  }

  onRowClick(row: PracticeAreaRow): void {
    if (!this.canManage || this.isCodeLoading(row.code)) return;
    if (row.kind === 'available') {
      this.add(row.code, row.name);
    } else if (row.kind === 'secondary') {
      this.remove(row.code, row.name);
    }
  }

  private add(code: string, name: string): void {
    this.loadingCodes.add(code);
    this.practiceAreaService
      .addEmployeeToPracticeArea(this.data.employeeId, code)
      .subscribe({
        next: () => {
          this.assigned = [
            ...this.assigned,
            {
              practiceAreaCode: code,
              practiceAreaName: name,
              assignedAt: '',
              source: EmployeePracticeAreaSource.Manual,
            },
          ];
          this.setRowKind(code, 'secondary');
          this.loadingCodes.delete(code);
          this.changed = true;
          this.snackbarService.showSuccess(
            `Added ${name} to ${this.data.userName}`,
          );
        },
        error: (err) => {
          this.loadingCodes.delete(code);
          this.snackbarService.showError(
            describeApiError(err, 'Failed to add practice area'),
          );
        },
      });
  }

  private remove(code: string, name: string): void {
    this.loadingCodes.add(code);
    this.practiceAreaService
      .removeEmployeeFromPracticeArea(this.data.employeeId, code)
      .subscribe({
        next: () => {
          this.assigned = this.assigned.filter(
            (a) => a.practiceAreaCode !== code,
          );
          this.setRowKind(code, 'available');
          this.loadingCodes.delete(code);
          this.changed = true;
          this.snackbarService.showSuccess(
            `Removed ${name} from ${this.data.userName}`,
          );
        },
        error: (err) => {
          this.loadingCodes.delete(code);
          this.snackbarService.showError(
            describeApiError(err, 'Failed to remove practice area'),
          );
        },
      });
  }

  close(): void {
    this.dialogRef.close(this.changed);
  }
}
